// Contract Management Types

export type ContractStatus =
  | 'draft'
  | 'review'
  | 'approved'
  | 'active'
  | 'completed'
  | 'expired'
  | 'cancelled'
  | 'rejected';
export type ContractType =
  | 'client'
  | 'subcontractor'
  | 'supplier'
  | 'vendor'
  | 'consultant'
  | 'lease'
  | 'service'
  | 'other';
export type ContractPartyType = 'internal' | 'external';
export type ApprovalStatus = 'pending' | 'approved' | 'rejected' | 'skipped';
export type MilestoneStatus = 'pending' | 'in_progress' | 'completed' | 'overdue' | 'cancelled';
export type CommentVisibility = 'all' | 'internal' | 'external';
export type SignatureStatus = 'pending' | 'signed' | 'declined';

export interface ContractMilestone {
  _id?: string;
  id?: string;
  title: string;
  description?: string;
  dueDate: string | Date;
  amount: number;
  currency: string;
  status: MilestoneStatus;
  completedDate?: string | Date;
  remarks?: string;
  attachments?: ContractAttachment[];
  createdAt: string | Date;
  updatedAt: string | Date;
}

export interface ContractVersion {
  versionNumber: number;
  fileName: string;
  fileUrl: string;
  fileSize: number;
  changeLog: string;
  uploadedBy: string;
  uploadedAt: string | Date;
}

export interface ContractApproval {
  userId: string;
  role: string;
  status: ApprovalStatus;
  approvedAt?: string | Date;
  comments?: string;
  signature?: string;
  order: number;
}

export interface ContractSignature {
  userId: string;
  signatureImageUrl: string;
  signedAt: string | Date;
  ipAddress: string;
  userAgent: string;
  browserFingerprint: string;
  geoLocation?: {
    latitude: number;
    longitude: number;
    accuracy: number;
  };
  macAddress?: string;
  consentConfirmed: boolean;
  signedPdfUrl: string;
}

export interface ContractAttachment {
  fileName: string;
  originalName: string;
  fileUrl: string;
  fileType: string;
  fileSize: number;
  uploadedBy: string;
  uploadedAt: string | Date;
  s3Key?: string;
}

export interface ContractParty {
  _id: string;
  id: string;
  name: string;
  type: ContractPartyType;
  contactPerson: string;
  email: string;
  phone: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  taxId?: string;
  registrationNumber?: string;
  isActive: boolean;
  createdAt: string | Date;
  updatedAt: string | Date;
}

export interface Contract {
  _id: string;
  id: string;
  contractNumber: string;
  title: string;
  description?: string;
  projectId: string;
  contractTypeId: string;
  partyId: string;
  status: ContractStatus;
  amount: number;
  currency: string;
  startDate: string | Date;
  endDate: string | Date;
  scope: string;

  // Milestones
  milestones: ContractMilestone[];

  // Version control
  versions: ContractVersion[];
  currentVersion: number;

  // Approval workflow
  approvals: ContractApproval[];
  currentApprovalStep: number;
  requiresApproval: boolean;

  // Digital signatures
  signatures: ContractSignature[];
  allPartiesSigned: boolean;
  signedDocumentUrl?: string;
  signedDate?: string | Date;

  // Collaboration
  collaborators: string[];
  watchers: string[];

  // Attachments
  attachments: ContractAttachment[];

  // Tracking
  createdBy: string;
  updatedBy?: string;
  createdAt: string | Date;
  updatedAt: string | Date;

  // Notifications
  reminderSettings: {
    enabled: boolean;
    daysBefore: number[];
    recipients: string[];
  };

  // Metadata
  tags: string[];
  customFields: Record<string, any>;
  isTemplate: boolean;
  templateId?: string;

  // Computed fields (from virtuals)
  daysUntilExpiration?: number;
  duration?: number;
  isExpired?: boolean;
  totalMilestoneAmount?: number;
  completedMilestoneAmount?: number;
  pendingApprovalsCount?: number;
  requiresUserSignature?: boolean;

  // Populated fields
  project?: {
    _id: string;
    name: string;
  };
  contractType?: {
    _id: string;
    name: string;
  };
  party?: ContractParty;
  creator?: {
    _id: string;
    name: string;
    email: string;
    firstName?: string;
    lastName?: string;
  };
  updater?: {
    _id: string;
    name: string;
    email: string;
    firstName?: string;
    lastName?: string;
  };
}

export interface ContractComment {
  _id: string;
  id: string;
  contractId: string;
  content: string;
  authorId: string;
  authorName: string;
  visibility: CommentVisibility;
  mentions: string[];
  attachments?: {
    fileName: string;
    fileUrl: string;
    fileType: string;
    fileSize: number;
  }[];
  isEdited: boolean;
  editedAt?: string | Date;
  parentCommentId?: string; // For threaded comments
  createdAt: string | Date;
  updatedAt: string | Date;

  // Populated fields
  author?: {
    _id: string;
    name: string;
    email: string;
    firstName?: string;
    lastName?: string;
  };
  mentionedUsers?: {
    _id: string;
    name: string;
    email: string;
  }[];

  // Computed fields
  repliesCount?: number;
  attachmentCount?: number;
  mentionCount?: number;
  isReply?: boolean;
}

// API Interfaces
export interface CreateContractData {
  title: string;
  description?: string;
  projectId: string;
  contractTypeId: string;
  partyId: string;
  amount: number;
  currency: string;
  startDate: string | Date;
  endDate: string | Date;
  scope: string;
  milestones?: Omit<ContractMilestone, '_id' | 'createdAt' | 'updatedAt'>[];
  attachments?: any[];
  collaborators?: string[];
  tags?: string[];
  customFields?: Record<string, any>;
  requiresApproval?: boolean;
  approvalWorkflow?: Array<{
    userId: string;
    role: string;
    order: number;
  }>;
  reminderSettings?: {
    enabled: boolean;
    daysBefore: number[];
    recipients: string[];
  };
}

export interface UpdateContractData extends Partial<CreateContractData> {
  status?: ContractStatus;
  currentApprovalStep?: number;
}

export interface ContractFilters {
  status?: ContractStatus | 'all';
  contractTypeId?: string;
  partyId?: string;
  search?: string;
  tags?: string[];
  dateRange?: {
    from?: Date;
    to?: Date;
  };
  amountRange?: {
    min?: number;
    max?: number;
  };
  createdBy?: string;
  collaborator?: string;
  requiresAction?: boolean; // Contracts needing user action
  expiringIn?: number; // Days
}

export interface ContractListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: ContractStatus | 'all';
  contractTypeId?: string;
  partyId?: string;
  tags?: string[];
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  dateFrom?: string;
  dateTo?: string;
  amountMin?: number;
  amountMax?: number;
  requiresAction?: boolean;
  expiringIn?: number;
}

export interface ContractListResponse {
  contracts: Contract[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  filters: {
    applied: ContractFilters;
    available: {
      statuses: ContractStatus[];
      contractTypes: Array<{ id: string; name: string }>;
      parties: Array<{ id: string; name: string }>;
      tags: string[];
    };
  };
  stats: {
    total: number;
    byStatus: Record<ContractStatus, number>;
    totalValue: number;
    expiringThisMonth: number;
    awaitingSignature: number;
    awaitingApproval: number;
  };
}

export interface CreateContractPartyData {
  name: string;
  type: ContractPartyType;
  contactPerson: string;
  email: string;
  phone: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  taxId?: string;
  registrationNumber?: string;
}

export interface UpdateContractPartyData extends Partial<CreateContractPartyData> {
  isActive?: boolean;
}

export interface CreateContractCommentData {
  content: string;
  visibility: CommentVisibility;
  mentions?: string[];
  attachments?: any[];
  parentCommentId?: string;
}

export interface ContractDashboardStats {
  overview: {
    totalContracts: number;
    activeContracts: number;
    totalValue: number;
    completedThisMonth: number;
  };
  status: {
    draft: number;
    review: number;
    approved: number;
    active: number;
    completed: number;
    expired: number;
    cancelled: number;
  };
  actionRequired: {
    awaitingApproval: number;
    awaitingSignature: number;
    expiringIn30Days: number;
    overdueMilestones: number;
  };
  trends: {
    contractsOverTime: Array<{ date: string; count: number }>;
    valueOverTime: Array<{ date: string; value: number }>;
  };
  milestones: {
    upcoming: number;
    overdue: number;
    completed: number;
    totalValue: number;
  };
}

export interface ApprovalAction {
  action: 'approve' | 'reject';
  comments?: string;
  signature?: string;
}

export interface DigitalSignatureData {
  signatureImageUrl: string;
  consentConfirmed: boolean;
  browserFingerprint: string;
  geoLocation?: {
    latitude: number;
    longitude: number;
    accuracy: number;
  };
  macAddress?: string;
}

export interface ContractSearchResult {
  id: string;
  contractNumber: string;
  title: string;
  status: ContractStatus;
  amount: number;
  currency: string;
  party: {
    name: string;
    type: ContractPartyType;
  };
  project: {
    name: string;
  };
}

// UI State Interfaces
export interface ContractFormData extends CreateContractData {
  id?: string;
}

export interface MilestoneFormData extends Partial<ContractMilestone> {
  isNew?: boolean;
}

export interface CommentFormData {
  content: string;
  visibility: CommentVisibility;
  mentions: string[];
  attachments: File[];
  parentCommentId?: string;
}

// Filter and Sort Options
export const CONTRACT_STATUSES: { value: ContractStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'review', label: 'Under Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'active', label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'expired', label: 'Expired' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'rejected', label: 'Rejected' },
];

export const CONTRACT_SORT_OPTIONS = [
  { value: 'createdAt', label: 'Created Date' },
  { value: 'updatedAt', label: 'Updated Date' },
  { value: 'title', label: 'Title' },
  { value: 'amount', label: 'Amount' },
  { value: 'startDate', label: 'Start Date' },
  { value: 'endDate', label: 'End Date' },
  { value: 'status', label: 'Status' },
];

export const MILESTONE_STATUSES: { value: MilestoneStatus; label: string; color: string }[] = [
  { value: 'pending', label: 'Pending', color: 'gray' },
  { value: 'in_progress', label: 'In Progress', color: 'blue' },
  { value: 'completed', label: 'Completed', color: 'green' },
  { value: 'overdue', label: 'Overdue', color: 'red' },
  { value: 'cancelled', label: 'Cancelled', color: 'gray' },
];

export const CONTRACT_STATUS_COLORS: Record<ContractStatus, string> = {
  draft: 'gray',
  review: 'yellow',
  approved: 'blue',
  active: 'green',
  completed: 'green',
  expired: 'red',
  cancelled: 'gray',
  rejected: 'red',
};

export const COMMENT_VISIBILITY_OPTIONS: { value: CommentVisibility; label: string }[] = [
  { value: 'all', label: 'Visible to All' },
  { value: 'internal', label: 'Internal Only' },
  { value: 'external', label: 'External Only' },
];

// Analytics interfaces
export interface ContractAnalytics {
  overview: {
    totalContracts: number;
    totalValue: number;
    averageValue: number;
    completedContracts: number;
    activeContracts: number;
    pendingApproval: number;
    completionRate: number;
  };
  statusBreakdown: Array<{
    _id: ContractStatus;
    count: number;
    totalValue: number;
  }>;
  typeBreakdown: Array<{
    _id: ContractType;
    count: number;
    totalValue: number;
    averageValue: number;
  }>;
  approvals: {
    requiresApproval: number;
    fullyApproved: number;
    pendingApproval: number;
    rejected: number;
    averageApprovalTime: number;
  };
  milestones: {
    totalMilestones: number;
    completedMilestones: number;
    overdueMilestones: number;
    completionRate: number;
    totalMilestoneValue: number;
    completedMilestoneValue: number;
    valueCompletionRate: number;
  };
  valueAnalysis: {
    totalValue: number;
    averageValue: number;
    minValue: number;
    maxValue: number;
    completedValue: number;
    activeValue: number;
  };
  recentActivity: Array<{
    _id: string;
    title: string;
    status: ContractStatus;
    amount: number;
    updatedAt: string;
    party?: {
      name: string;
    };
  }>;
  generatedAt: string;
}

export interface ContractTrends {
  period: 'daily' | 'weekly' | 'monthly' | 'yearly';
  trends: Array<{
    period: string;
    contractsCreated: number;
    totalValue: number;
    averageValue: number;
    completedContracts: number;
    completionRate: number;
    statusDistribution: Record<ContractStatus, number>;
  }>;
}

export interface ContractPerformance {
  completion: {
    completionRate: number;
    averageCompletionTime: number;
    onTimeRate: number;
  };
  approvals: {
    approvalRate: number;
    rejectionRate: number;
    averageApprovalTime: number;
  };
  milestones: {
    completionRate: number;
    onTimeRate: number;
    overdueRate: number;
  };
  timing: {
    averageContractDuration: number;
    averageTimeToApproval: number;
    shortestContract: number;
    longestContract: number;
  };
}

export interface AnalyticsFilters {
  projectId?: string;
  dateRange?: string;
  status?: ContractStatus | 'all';
  type?: ContractType | 'all';
  period?: 'daily' | 'weekly' | 'monthly' | 'yearly';
}
